// Volume preferences (0..1) for the three mixes the game has: the looping
// background tune, every one-shot effect (dice, turn bell, cards, ...), and the
// clock ticking that runs while you are on a timer.
//
// The ticking is its own mix rather than part of the effects one because it is
// the only sound that plays *continuously* — it's the one people want to turn
// down (or off) without losing the dice and the money.
//
// Kept deliberately free of any audio imports so the audio manager can depend on
// this and not the other way round: changes are written to localStorage and
// announced on window, and audioManager listens and re-mixes live.
const MUSIC_VOLUME_KEY = "musicVolume"
const SOUND_EFFECTS_VOLUME_KEY = "soundEffectsVolume"
const TIMER_VOLUME_KEY = "timerSoundVolume"

export const MUSIC_VOLUME_CHANGED_EVENT = "music-volume-changed"
export const SOUND_EFFECTS_VOLUME_CHANGED_EVENT = "sound-effects-volume-changed"
export const TIMER_VOLUME_CHANGED_EVENT = "timer-volume-changed"

// The tune plays at its full recorded level when the slider is at 100%, so the
// slider starts a quarter of the way up: the music is background, sitting under
// the dice and the money, with plenty of room to turn it up.
export const DEFAULT_MUSIC_VOLUME = 0.25
export const DEFAULT_EFFECTS_VOLUME = 1
// The tick sits under everything else for as long as a timer runs, so it starts
// at half: present enough to feel the pressure, quiet enough to ignore.
export const DEFAULT_TIMER_VOLUME = 0.5

export const clampVolume = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.min(Math.max(value, 0), 1)
}

const readVolume = (key: string, fallback: number): number => {
  try {
    const stored = localStorage.getItem(key)
    if (stored === null) return fallback
    const parsed = parseFloat(stored)
    if (!Number.isFinite(parsed)) return fallback
    return clampVolume(parsed)
  } catch {
    // Private-mode / storage-disabled browsers still get sound, just not the
    // memory of how loud it was.
    return fallback
  }
}

const writeVolume = (key: string, eventName: string, value: number): number => {
  const clamped = clampVolume(value)
  try {
    localStorage.setItem(key, String(clamped))
  } catch {}
  window.dispatchEvent(new CustomEvent(eventName, { detail: clamped }))
  return clamped
}

export const getMusicVolume = (): number =>
  readVolume(MUSIC_VOLUME_KEY, DEFAULT_MUSIC_VOLUME)

export const setMusicVolume = (value: number): number =>
  writeVolume(MUSIC_VOLUME_KEY, MUSIC_VOLUME_CHANGED_EVENT, value)

export const getSoundEffectsVolume = (): number =>
  readVolume(SOUND_EFFECTS_VOLUME_KEY, DEFAULT_EFFECTS_VOLUME)

export const setSoundEffectsVolume = (value: number): number =>
  writeVolume(
    SOUND_EFFECTS_VOLUME_KEY,
    SOUND_EFFECTS_VOLUME_CHANGED_EVENT,
    value
  )

export const getTimerVolume = (): number =>
  readVolume(TIMER_VOLUME_KEY, DEFAULT_TIMER_VOLUME)

export const setTimerVolume = (value: number): number =>
  writeVolume(TIMER_VOLUME_KEY, TIMER_VOLUME_CHANGED_EVENT, value)
